import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@material-ui/core';
import { DateTimePicker } from '@material-ui/pickers';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment-timezone';
import {
  createInterview,
  interviewsCreatingSelector
} from '../../redux/features/interviews/interviewsSlice';
import { messageUpdated } from '../../redux/features/app/appSlice';
import { Severity } from '../Message/Message';
import {
  trackInterviewScheduled,
  trackInterviewSchedulingError,
  trackScheduleInterviewCancelled
} from '../../helpers/analytics';

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    paddingTop: theme.spacing(2)
  },
  formControl: {
    width: '100%'
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(3)
  }
}));

// Duration options in minutes
const DURATION_OPTIONS = [
  { value: 15, label: '15 minutes' },
  { value: 30, label: '30 minutes' },
  { value: 45, label: '45 minutes' },
  { value: 60, label: '1 hour' },
  { value: 90, label: '1.5 hours' },
  { value: 120, label: '2 hours' }
];

// Common timezones
const TIMEZONE_OPTIONS = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Asia/Dubai',
  'Australia/Sydney',
  'UTC'
];

const ScheduleInterviewDialog = ({
  open,
  onClose,
  candidateUserId,
  candidateName,
  candidateEmail,
  jobId,
  jobTitle,
  applicationId,
  companyId
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const creating = useSelector(interviewsCreatingSelector);

  // Form state
  const [interviewDate, setInterviewDate] = useState(
    moment().add(1, 'day').hour(10).minute(0)
  );
  const [durationMinutes, setDurationMinutes] = useState(60);
  const [locationUrl, setLocationUrl] = useState('');
  const [interviewerName, setInterviewerName] = useState('');
  const [timezone, setTimezone] = useState(
    moment.tz.guess() || 'America/New_York'
  );
  const [description, setDescription] = useState('');

  // Validation errors
  const [errors, setErrors] = useState({});

  const handleClose = () => {
    if (!creating) {
      // Track cancellation
      if (window.gtag) {
        trackScheduleInterviewCancelled(candidateUserId, candidateName);
      }
      resetForm();
      onClose();
    }
  };

  const resetForm = () => {
    setInterviewDate(moment().add(1, 'day').hour(10).minute(0));
    setDurationMinutes(60);
    setLocationUrl('');
    setInterviewerName('');
    setTimezone(moment.tz.guess() || 'America/New_York');
    setDescription('');
    setErrors({});
  };

  const validate = () => {
    const newErrors = {};

    if (!interviewDate || !interviewDate.isValid()) {
      newErrors.interviewDate = 'Please select a valid date and time';
    } else if (interviewDate.isBefore(moment())) {
      newErrors.interviewDate = 'Interview date must be in the future';
    }

    if (!durationMinutes) {
      newErrors.durationMinutes = 'Please select a duration';
    }

    if (!timezone) {
      newErrors.timezone = 'Please select a timezone';
    }

    if (locationUrl && locationUrl.length > 500) {
      newErrors.locationUrl = 'Location URL is too long (max 500 characters)';
    }

    if (interviewerName && interviewerName.length > 255) {
      newErrors.interviewerName =
        'Interviewer name is too long (max 255 characters)';
    }

    if (description && description.length > 2000) {
      newErrors.description = 'Description is too long (max 2000 characters)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      dispatch(
        messageUpdated({
          text: 'Please fix the validation errors',
          severity: Severity.ERROR
        })
      );
      return;
    }

    const interviewData = {
      companyId,
      candidateUserId,
      jobId,
      applicationId,
      interviewDate: interviewDate.utc().format('YYYY-MM-DDTHH:mm:ss'),
      durationMinutes,
      locationUrl: locationUrl || null,
      interviewerName: interviewerName || null,
      timezone,
      description: description || null,
      candidateEmail,
      candidateName,
      jobTitle
    };

    console.log(
      '📅 [ScheduleInterviewDialog] Submitting interview:',
      interviewData
    );

    try {
      dispatch(createInterview(interviewData));

      dispatch(
        messageUpdated({
          text: `Interview scheduled successfully with ${candidateName}`,
          severity: Severity.SUCCESS
        })
      );

      // Track analytics
      if (window.gtag) {
        const locationType = locationUrl
          ? locationUrl.startsWith('http')
            ? 'url'
            : 'physical'
          : 'not_specified';

        trackInterviewScheduled(
          candidateUserId,
          candidateName,
          jobId,
          jobTitle,
          durationMinutes,
          locationType
        );
      }

      resetForm();
      onClose();
    } catch (error) {
      console.error('❌ [ScheduleInterviewDialog] Error:', error);

      // Track error
      if (window.gtag) {
        trackInterviewSchedulingError(
          'submission_failed',
          error.message || 'Unknown error'
        );
      }

      dispatch(
        messageUpdated({
          text: `Failed to schedule interview: ${error}`,
          severity: Severity.ERROR
        })
      );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        // Prevent closing on backdrop click or escape when creating
        if (
          creating &&
          (reason === 'backdropClick' || reason === 'escapeKeyDown')
        ) {
          return;
        }
        handleClose();
      }}
      maxWidth="md"
      fullWidth>
      <DialogTitle>Schedule Interview with {candidateName}</DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Job: <strong>{jobTitle}</strong>
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Candidate: <strong>{candidateName}</strong> ({candidateEmail})
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <DateTimePicker
              label="Interview Date & Time"
              value={interviewDate}
              onChange={setInterviewDate}
              disablePast
              format="YYYY-MM-DD HH:mm"
              inputVariant="outlined"
              fullWidth
              minutesStep={15}
              error={!!errors.interviewDate}
              helperText={errors.interviewDate}
              disabled={creating}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl
              variant="outlined"
              className={classes.formControl}
              error={!!errors.timezone}>
              <InputLabel>Timezone</InputLabel>
              <Select
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                label="Timezone"
                disabled={creating}>
                {TIMEZONE_OPTIONS.map((tz) => (
                  <MenuItem key={tz} value={tz}>
                    {tz}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl
              variant="outlined"
              className={classes.formControl}
              error={!!errors.durationMinutes}>
              <InputLabel>Duration</InputLabel>
              <Select
                value={durationMinutes}
                onChange={(e) => setDurationMinutes(e.target.value)}
                label="Duration"
                disabled={creating}>
                {DURATION_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Interviewer Name"
              value={interviewerName}
              onChange={(e) => setInterviewerName(e.target.value)}
              variant="outlined"
              fullWidth
              placeholder="John Smith"
              error={!!errors.interviewerName}
              helperText={errors.interviewerName}
              disabled={creating}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Meeting Location/URL"
              value={locationUrl}
              onChange={(e) => setLocationUrl(e.target.value)}
              variant="outlined"
              fullWidth
              placeholder="https://meet.google.com/abc-defg-hij or Office Room 301"
              error={!!errors.locationUrl}
              helperText={
                errors.locationUrl ||
                'Zoom, Google Meet, Teams link, or physical location'
              }
              disabled={creating}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Description / Additional Notes (Optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              variant="outlined"
              fullWidth
              multiline
              minRows={4}
              placeholder="Please prepare questions about your experience with React..."
              error={!!errors.description}
              helperText={errors.description}
              disabled={creating}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={creating}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={creating}
          startIcon={creating && <CircularProgress size={20} />}>
          {creating ? 'Scheduling...' : 'Send Invitation'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ScheduleInterviewDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  candidateUserId: PropTypes.string.isRequired,
  candidateName: PropTypes.string.isRequired,
  candidateEmail: PropTypes.string.isRequired,
  jobId: PropTypes.string.isRequired,
  jobTitle: PropTypes.string.isRequired,
  applicationId: PropTypes.string.isRequired,
  companyId: PropTypes.string.isRequired
};

export default ScheduleInterviewDialog;
